import React from 'react';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import type { Folder } from 'types/library';

interface Props {
    folder: Folder;
    variant: 'grid' | 'list';
    onClick: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onMove: () => void;
}

const FOLDER_COLORS: Record<string, { start: string; end: string }> = {
    '#4ADE80': { start: '#6EF09A', end: '#16A34A' },
    '#60A5FA': { start: '#93C5FD', end: '#2563EB' },
    '#F87171': { start: '#FCA5A5', end: '#DC2626' },
    '#FBBF24': { start: '#FDE68A', end: '#D97706' },
    '#A78BFA': { start: '#C4B5FD', end: '#7C3AED' },
    '#34D399': { start: '#6EE7B7', end: '#059669' },
    '#FB923C': { start: '#FDBA74', end: '#EA580C' },
    '#E879F9': { start: '#F0ABFC', end: '#C026D3' },
};

const DEFAULT_COLORS = { start: '#FDE68A', end: '#D97706' };

const FolderSvg: React.FC<{ id: string | number; color: string; size?: number }> = ({ id, color, size = 44 }) => {
    const { start, end } = FOLDER_COLORS[color] ?? DEFAULT_COLORS;
    const h = Math.round(size * (90 / 122));
    const gBack = `fb-${id}`;
    const gFront = `ff-${id}`;
    const clip = `fc-${id}`;
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={h} viewBox="0 0 122 90" fill="none" style={{ display: 'block', flexShrink: 0, transition: 'transform 0.2s ease' }}>
            <g clipPath={`url(#${clip})`}>
                <path d="M120.085 14.1861V83.5406C120.085 87.1075 117.184 90 113.607 90H8.55106C4.97387 90 2.073 87.1075 2.073 83.5406V6.45939C2.073 2.89252 4.97387 0 8.55106 0H31.6246C33.6237 0 35.5114 0.921109 36.7384 2.49462L40.8196 7.72672H113.607C117.184 7.72672 120.085 10.6192 120.085 14.1861Z" fill={`url(#${gBack})`} />
                <path d="M112.515 12.4408H9.64457C8.21348 12.4408 7.05334 13.5976 7.05334 15.0246V77.5476C7.05334 78.9746 8.21348 80.1314 9.64457 80.1314H112.515C113.946 80.1314 115.106 78.9746 115.106 77.5476V15.0246C115.106 13.5976 113.946 12.4408 112.515 12.4408Z" fill="white" />
                <path d="M4.66422 17.7207H117.494C118.924 17.7207 120.085 18.8782 120.085 20.3045V84.8325C120.085 87.685 117.763 90 114.903 90H7.25545C4.39473 90 2.073 87.685 2.073 84.8325V20.3045C2.073 18.8782 3.23387 17.7207 4.66422 17.7207Z" fill={`url(#${gFront})`} />
            </g>
            <defs>
                <linearGradient id={gBack} x1="61" y1="0" x2="61" y2="90" gradientUnits="userSpaceOnUse">
                    <stop stopColor={start} /><stop offset="0.27" stopColor={end} />
                </linearGradient>
                <linearGradient id={gFront} x1="61" y1="17.72" x2="61" y2="90" gradientUnits="userSpaceOnUse">
                    <stop stopColor={start} /><stop offset="1" stopColor={end} />
                </linearGradient>
                <clipPath id={clip}><rect width="122" height="90" fill="white" /></clipPath>
            </defs>
        </svg>
    );
};

const MoreBtn: React.FC<{ onClick: (e: React.MouseEvent) => void }> = ({ onClick }) => (
    <button
        className="flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent text-text-muted opacity-0 transition-all group-hover:opacity-100 hover:bg-bg-hover hover:text-text-primary"
        onClick={onClick}
    >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" />
        </svg>
    </button>
);

const FolderCard: React.FC<Props> = ({ folder, variant, onClick, onEdit, onDelete, onMove }) => {
    const menu: MenuProps['items'] = [
        { key: 'edit', label: 'Đổi tên / màu', onClick: e => { e.domEvent.stopPropagation(); onEdit(); } },
        { key: 'move', label: 'Di chuyển', onClick: e => { e.domEvent.stopPropagation(); onMove(); } },
        { type: 'divider' },
        { key: 'delete', label: 'Xóa', danger: true, onClick: e => { e.domEvent.stopPropagation(); onDelete(); } },
    ];

    if (variant === 'list') {
        return (
            <div className="group lib-list-row" onClick={onClick} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && onClick()}>
                <div className="lib-list-row__icon">
                    <FolderSvg id={`l-${folder.id}`} color={folder.color} size={28} />
                </div>
                <span className="lib-list-row__name">{folder.name}</span>
                <span className="lib-list-row__cell">—</span>
                <span className="lib-list-row__cell">{folder.updated_at.slice(0, 10)}</span>
                <Dropdown menu={{ items: menu }} trigger={['click']} placement="bottomRight">
                    <MoreBtn onClick={e => e.stopPropagation()} />
                </Dropdown>
            </div>
        );
    }

    return (
        <div
            className="group lib-folder-card"
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && onClick()}
        >
            <div className="lib-folder-card__icon">
                <FolderSvg id={folder.id} color={folder.color} size={44} />
            </div>
            <span className="lib-folder-card__name" title={folder.name}>
                {folder.name}
            </span>
            <Dropdown menu={{ items: menu }} trigger={['click']} placement="bottomRight">
                <MoreBtn onClick={e => e.stopPropagation()} />
            </Dropdown>
        </div>
    );
};

export default FolderCard;
